// Evidence exception routes of the host the page runs in: the tenant's for the office, the portal's for a subcontractor.
import { HttpClient } from '@angular/common/http';
import { ErrorHandler, Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { EvidenceExceptionTag, Task, TaskEvidenceExceptionRequest } from '@jms/model';
import { injectWorkBuddyHost } from '@workbuddy/services/host/workbuddy.host.service';
import { SurfaceContextLike } from '@workbuddy/ui/v2/surface.engine';

@Injectable({ providedIn: 'root' })
export class TaskEvidenceExceptionService {

  private readonly http = inject(HttpClient);
  private readonly host = injectWorkBuddyHost();
  private readonly errorHandler = inject(ErrorHandler);
  // Per mounted page, so a slower older read never lands over a newer one.
  private readonly latestRead = new WeakMap<object, number>();

  public reasons(): Promise<EvidenceExceptionTag[]> {

    return firstValueFrom(this.http.get<EvidenceExceptionTag[]>(this.host.evidenceExceptionReasonsApi));
  }

  public record(taskId: string, request: TaskEvidenceExceptionRequest): Promise<Task> {

    return firstValueFrom(this.http.post<Task>(`${this.host.taskApi}/${taskId}/evidence-exception`, request));
  }

  public remove(taskId: string): Promise<Task> {

    return firstValueFrom(this.http.delete<Task>(`${this.host.taskApi}/${taskId}/evidence-exception`));
  }

  /** Rereads the two server-derived fields after a write, since its answer may predate the outcome refresh; a clean merge, so unsaved edits and uploads in flight stay as they are. */
  public async refreshDerivedEvidence(page: Pick<SurfaceContextLike<Task>, '$model' | 'patchModel'>): Promise<void> {

    const taskId = page.$model()?._id;
    if (!taskId) return;
    const read = (this.latestRead.get(page) ?? 0) + 1;
    this.latestRead.set(page, read);
    try {
      const fresh = await firstValueFrom(this.http.get<Task>(`${this.host.taskApi}/${taskId}`));
      if (this.latestRead.get(page) !== read) return;
      // A bound field resets `undefined` to the value the page opened with, so a cleared exception must arrive as null or the spent exception comes back.
      page.patchModel?.({ evidenceException: fresh.evidenceException ?? null, evidenceOutcome: fresh.evidenceOutcome });
    } catch (error) {
      this.errorHandler.handleError(error);
    }
  }
}
